import * as React from 'react'
import { Link, useParams } from 'react-router'
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  CopyPlus,
  Loader2,
  Mail,
  Pencil,
  RotateCcw,
} from 'lucide-react'
import type { ProposalDetailDto } from '@lib/dto/proposal.dto'
import { parseUuid } from '@lib/validators/uuid.validator'
import { updateProposalInputSchema } from '@lib/input/proposal.input'
import { useProposal } from '@/hooks/useProposal'
import {
  useAddProposalVersion,
  useArchiveProposal,
  useUpdateProposal,
} from '@/hooks/useProposalMutations'
import { errorMessage } from '@/helpers/logger'
import { ProposalStatusBadge } from '@/components/ClientBadges'
import { ProposalDraftVersion } from '@/components/ProposalDraftVersion'
import { ProposalSentVersion } from '@/components/ProposalSentVersion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * One proposal, organized around what its latest version needs next.
 *
 * A draft is something to finish and send; a sent version is something to
 * follow up. The two have almost nothing in common on screen, so the latest
 * version is shown as whichever it is, and the versions before it collapse to
 * a line each — they are history, and the page is about what happens next.
 */
export function ProposalDetailPage() {
  const params = useParams<{ id: string }>()
  const proposalId = parseUuid(params.id)

  const proposal = useProposal(proposalId ?? '')
  const archiveProposal = useArchiveProposal()
  const addVersion = useAddProposalVersion()

  const [actionError, setActionError] = React.useState<string | null>(null)
  const [deliverError, setDeliverError] = React.useState<string | null>(null)

  if (proposalId !== null && proposal.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
      </div>
    )
  }

  if (proposalId === null || proposal.isError || !proposal.data) {
    return (
      <div className="space-y-4">
        <Link
          to="/proposals"
          className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Proposals
        </Link>
        <div className="rounded-lg border border-default bg-surface px-3 py-12 text-center">
          <p className="text-sm font-medium">Proposal not found</p>
        </div>
      </div>
    )
  }

  const data = proposal.data
  // A closure does not keep the narrowing from the returns above.
  const id = proposalId
  const isArchived = data.archivedAt !== null
  const [latest, ...earlier] = data.versions
  // A new version can only start when the current one is settled — an open
  // draft IS the next version, so offering to create another would be wrong.
  const canStartVersion = latest !== undefined && latest.sentAt !== null
  // Only one version of a proposal can be accepted, and that rule lives across
  // versions rather than in any single one's status — so it is resolved here,
  // where the whole set is in hand, and passed down. Otherwise a card would
  // offer an action the API is going to refuse.
  const hasAcceptedVersion = data.versions.some(
    (version) => version.acceptedAt !== null,
  )

  async function handleArchiveToggle(): Promise<void> {
    setActionError(null)
    try {
      await archiveProposal.mutateAsync({
        id,
        archived: !isArchived,
      })
    } catch (caught) {
      setActionError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleAddVersion(): Promise<void> {
    setActionError(null)
    try {
      await addVersion.mutateAsync({ proposalId: id, input: {} })
    } catch (caught) {
      setActionError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  function renderLatest(): React.ReactNode {
    if (latest === undefined) return null
    if (latest.isEditable) {
      return (
        <ProposalDraftVersion
          proposal={data}
          version={latest}
          onDeliverError={setDeliverError}
        />
      )
    }
    return (
      <ProposalSentVersion
        proposal={data}
        version={latest}
        hasAcceptedVersion={hasAcceptedVersion}
        collapsible={false}
      />
    )
  }

  return (
    <div className="space-y-6">
      <Link
        to={`/clients/${data.clientId}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-text hover:text-primary-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {data.clientName}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <ProposalTitle proposal={data}>
            {latest ? <ProposalStatusBadge status={latest.status} /> : null}
            {isArchived ? (
              <span className="text-xs text-muted-text">Archived</span>
            ) : null}
          </ProposalTitle>
          <Recipient proposal={data} />
        </div>
        <div className="flex items-center gap-2">
          {canStartVersion && !isArchived ? (
            <Button
              variant="outline"
              onClick={() => {
                void handleAddVersion()
              }}
              disabled={addVersion.isPending}
            >
              {addVersion.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CopyPlus className="h-3.5 w-3.5" />
              )}
              New version
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => {
              void handleArchiveToggle()
            }}
            disabled={archiveProposal.isPending}
          >
            {isArchived ? (
              <RotateCcw className="h-3.5 w-3.5" />
            ) : (
              <Archive className="h-3.5 w-3.5" />
            )}
            {isArchived ? 'Restore' : 'Archive'}
          </Button>
        </div>
      </header>

      {actionError ? (
        <p className="text-sm text-destructive">{actionError}</p>
      ) : null}
      {deliverError === null ? null : (
        <p className="text-sm text-destructive">{deliverError}</p>
      )}

      {renderLatest()}

      {earlier.length === 0 ? null : (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-secondary-text">
            Earlier versions
          </h2>
          <ol className="space-y-2">
            {earlier.map((version) => (
              <li key={version.id}>
                <ProposalSentVersion
                  proposal={data}
                  version={version}
                  hasAcceptedVersion={hasAcceptedVersion}
                  collapsible
                />
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}

/**
 * The proposal's title, renamable in place.
 *
 * It prints on the cover, so a typo found after creating the proposal has to
 * be fixable here rather than by starting again.
 */
function ProposalTitle({
  proposal,
  children,
}: {
  proposal: ProposalDetailDto
  /** Badges shown after the title, before the rename control. */
  children: React.ReactNode
}) {
  const updateProposal = useUpdateProposal()
  const [editing, setEditing] = React.useState<boolean>(false)
  const [value, setValue] = React.useState<string>(proposal.title)
  const [error, setError] = React.useState<string | null>(null)

  async function handleSave(): Promise<void> {
    const parsed = updateProposalInputSchema.safeParse({ title: value })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a title')
      return
    }
    if (parsed.data.title === proposal.title) {
      setEditing(false)
      return
    }
    setError(null)
    try {
      await updateProposal.mutateAsync({ id: proposal.id, input: parsed.data })
      setEditing(false)
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  if (!editing) {
    return (
      <div className="group flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold">{proposal.title}</h1>
        {children}
        <button
          type="button"
          onClick={() => {
            setValue(proposal.title)
            setError(null)
            setEditing(true)
          }}
          aria-label="Rename proposal"
          title="Rename"
          className="rounded-sm p-1 text-muted-text opacity-0 transition-opacity group-hover:opacity-100 hover:text-primary-text focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        void handleSave()
      }}
    >
      <Input
        aria-label="Proposal title"
        autoFocus
        className="w-96 max-w-full text-base font-semibold"
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setEditing(false)
        }}
      />
      <Button type="submit" size="sm" disabled={updateProposal.isPending}>
        Save
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => {
          setEditing(false)
        }}
      >
        Cancel
      </Button>
      {error === null ? null : (
        <p className="w-full text-xs text-destructive">{error}</p>
      )}
    </form>
  )
}

/**
 * Who this goes to, said where the proposal is rather than only in the Send
 * dialog. The recipient is the client's primary contact, so the fix for a
 * wrong or missing one is on the client, and the link goes there.
 */
function Recipient({ proposal }: { proposal: ProposalDetailDto }) {
  const clientLink = (
    <Link
      to={`/clients/${proposal.clientId}`}
      className="text-brand underline-offset-2 hover:underline"
    >
      {proposal.recipient === null ? 'Add one' : 'Change'}
    </Link>
  )

  if (proposal.recipient === null) {
    return (
      <p className="flex items-start gap-1.5 text-sm text-warning">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {proposal.clientName} has no primary contact to send this to.{' '}
          {clientLink}
        </span>
      </p>
    )
  }

  const { name, title, email } = proposal.recipient
  if (email === null) {
    return (
      <p className="flex items-start gap-1.5 text-sm text-warning">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          {name} has no email address, so this can only be marked sent.{' '}
          <Link
            to={`/clients/${proposal.clientId}`}
            className="text-brand underline-offset-2 hover:underline"
          >
            Add one
          </Link>
        </span>
      </p>
    )
  }

  return (
    <p className="flex items-start gap-1.5 text-sm text-muted-text">
      <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        To <span className="text-secondary-text">{name}</span>
        {title === null ? '' : `, ${title}`} · {email} {clientLink}
      </span>
    </p>
  )
}
